import { useEffect, useState } from 'react';
import type { PortfolioConnectionPublic, PortfolioPluginKind } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { emptySetupFields, fieldsFromConnection } from './sourceConnectionForm';
import { PortfolioMissingPhoneAlert } from './PortfolioMissingPhoneAlert';
import { PortfolioSyncWarning } from './PortfolioSyncWarning';

type SourceFieldsProps = {
  tenantId?: string;
  sourceConnectionId: string | null;
  sourceConnection?: PortfolioConnectionPublic | null;
  onBound: (connectionId: string | null, connection?: PortfolioConnectionPublic | null) => void;
  /** Solo Setup, sin selector de fuente ni encabezado (lo usa la receta Nuevo Cobrador). */
  setupOnly?: boolean;
};

export function ContactAgentSourceFields({
  tenantId,
  sourceConnectionId,
  sourceConnection,
  onBound,
  setupOnly = false,
}: SourceFieldsProps) {
  const initial = fieldsFromConnection(sourceConnection);
  const [pluginKind, setPluginKind] = useState<PortfolioPluginKind>(
    setupOnly ? 'SETUP_MSSQL' : sourceConnection?.pluginKind ?? 'EXCEL',
  );
  const [connections, setConnections] = useState<PortfolioConnectionPublic[]>([]);
  const [name, setName] = useState(initial.name);
  const [host, setHost] = useState(initial.host);
  const [port, setPort] = useState(initial.port);
  const [database, setDatabase] = useState(initial.database);
  const [username, setUsername] = useState(initial.username);
  const [password, setPassword] = useState('');
  const [idempresa, setIdempresa] = useState(initial.idempresa);
  const [useCobradorViews, setUseCobradorViews] = useState(initial.useCobradorViews);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const applyFields = (connection: PortfolioConnectionPublic | null) => {
    const fields = connection ? fieldsFromConnection(connection) : emptySetupFields();
    setPluginKind(fields.pluginKind === 'EXCEL' ? 'SETUP_MSSQL' : fields.pluginKind);
    setName(fields.name);
    setHost(fields.host);
    setPort(fields.port);
    setDatabase(fields.database);
    setUsername(fields.username);
    setIdempresa(fields.idempresa);
    setUseCobradorViews(fields.useCobradorViews);
  };

  useEffect(() => {
    if (!tenantId) {
      return;
    }
    void contactAgentApi
      .listPortfolioConnections()
      .then(setConnections)
      .catch(err => setStatus(contactAgentError(err)));
  }, [tenantId]);

  useEffect(() => {
    if (!sourceConnection) {
      return;
    }
    applyFields(sourceConnection);
    setPluginKind(sourceConnection.pluginKind);
  }, [sourceConnection?.id]);

  const selectExisting = (connectionId: string) => {
    if (!connectionId) {
      applyFields(null);
      onBound(null, null);
      return;
    }
    const selected = connections.find(item => item.id === connectionId) ?? null;
    applyFields(selected);
    onBound(connectionId, selected);
  };

  const saveConnection = async () => {
    setBusy(true);
    setStatus('');
    try {
      const payload = {
        pluginKind: 'SETUP_MSSQL' as const,
        name: name.trim().slice(0, 120) || 'Setup',
        host: host.trim(),
        port: Number(port) || 1433,
        database: database.trim(),
        username: username.trim(),
        password: password.trim() || undefined,
        options: {
          idempresa: Number(idempresa),
          syncIntervalMinutes: 30,
          portfolioSource: useCobradorViews ? 'views' : 'tables',
        },
      };
      const saved = sourceConnectionId
        ? await contactAgentApi.updatePortfolioConnection(sourceConnectionId, payload)
        : await contactAgentApi.createPortfolioConnection(payload);
      setConnections(current => {
        const rest = current.filter(item => item.id !== saved.id);
        return [saved, ...rest];
      });
      onBound(saved.id, saved);
      setPassword('');
      setStatus('Conexion guardada.');
    } catch (err) {
      setStatus(contactAgentError(err));
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (kind: 'test' | 'sync' | 'sync-confirm') => {
    if (!sourceConnectionId) {
      setStatus('Guarda la conexion primero.');
      return;
    }
    setBusy(true);
    setStatus('');
    try {
      if (kind === 'test') {
        await contactAgentApi.testPortfolioConnection(sourceConnectionId);
        setStatus('Conexion OK.');
        return;
      }
      const synced = await contactAgentApi.syncPortfolioConnection(sourceConnectionId, {
        confirmMassClose: kind === 'sync-confirm',
      });
      onBound(synced.id, synced);
      setStatus(
        synced.lastSyncAt
          ? `Ultima sync: ${new Date(synced.lastSyncAt).toLocaleString()}`
          : 'Sync lista.',
      );
    } catch (err) {
      setStatus(contactAgentError(err));
    } finally {
      setBusy(false);
    }
  };

  const setupConnections = connections.filter(
    item => item.pluginKind === 'SETUP_MSSQL' && (!tenantId || item.tenantId === tenantId),
  );

  return (
    <div className={setupOnly ? 'space-y-3' : 'space-y-3 rounded-xl border border-primary/20 bg-muted/30 p-4'}>
      {setupOnly ? null : (
      <div>
        <p className='text-sm font-medium'>Cartera de deudores (Setup)</p>
        <p className='text-xs text-muted-foreground'>
          Cada tenant tiene su propio catálogo de conexiones (host, base, idempresa y opciones). Lo que guardás acá
          aplica solo a este tenant, no a otros.
        </p>
      </div>
      )}
      {setupOnly ? null : (
      <div className='space-y-2'>
        <Label htmlFor='pluginKind'>Fuente de cartera</Label>
        <select
          id='pluginKind'
          className='flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
          value={pluginKind}
          onChange={event => {
            const next = event.target.value as PortfolioPluginKind;
            setPluginKind(next);
            if (next === 'EXCEL') {
              onBound(null, null);
            }
          }}
        >
          <option value='EXCEL'>Excel / CSV (importacion)</option>
          <option value='SETUP_MSSQL'>Setup (SQL Server)</option>
        </select>
      </div>
      )}
      {pluginKind === 'EXCEL' ? (
        <p className='text-sm text-muted-foreground'>
          Este agente usa la cartera importada. El bot habla con la ultima carga de Excel.
          {setupConnections.length > 0
            ? ` Hay ${setupConnections.length} conexion(es) Setup guardadas: elegí Setup (SQL Server) para reutilizarlas.`
            : ''}
        </p>
      ) : (
        <div className='space-y-3'>
          <div className='space-y-2'>
            <Label htmlFor='existingConnection'>Conexion existente</Label>
            <select
              id='existingConnection'
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
              value={sourceConnectionId ?? ''}
              onChange={event => selectExisting(event.target.value)}
            >
              <option value=''>Nueva conexion</option>
              {setupConnections.map(item => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className='grid gap-3 sm:grid-cols-2'>
            <Field label='Nombre' id='connName' value={name} onChange={setName} />
            <Field label='Host IPv4' id='connHost' value={host} onChange={setHost} />
            <Field label='Puerto' id='connPort' value={port} onChange={setPort} />
            <Field label='Base' id='connDb' value={database} onChange={setDatabase} />
            <Field label='Usuario' id='connUser' value={username} onChange={setUsername} />
            <Field
              label={sourceConnection?.hasPassword ? 'Password (dejar vacio para no cambiar)' : 'Password'}
              id='connPass'
              value={password}
              onChange={setPassword}
              type='password'
            />
            <Field label='idempresa' id='connEmpresa' value={idempresa} onChange={setIdempresa} />
          </div>
          <label className='flex items-start gap-2 text-sm'>
            <input
              type='checkbox'
              className='mt-1'
              checked={useCobradorViews}
              onChange={event => setUseCobradorViews(event.target.checked)}
            />
            <span>
              Maestro desde vista <code className='text-xs'>Clientes</code> (campo <code className='text-xs'>nombre</code>)
              para <strong>esta conexión</strong>. En otras conexiones del mismo u otro tenant podés dejarlo apagado (modo
              tablas / acobrar). La deuda sigue en <code className='text-xs'>acobrar</code> (cantidad × cotización).
            </span>
          </label>
          <div className='flex flex-wrap gap-2'>
            <Button type='button' variant='outline' disabled={busy} onClick={() => void saveConnection()}>
              Guardar conexion
            </Button>
            <Button type='button' variant='ghost' disabled={busy} onClick={() => void runAction('test')}>
              Probar
            </Button>
            <Button type='button' variant='ghost' disabled={busy} onClick={() => void runAction('sync')}>
              Sincronizar ahora
            </Button>
          </div>
          {sourceConnection?.lastSyncAt ? (
            <p className='text-xs text-muted-foreground'>
              Ultima sync: {new Date(sourceConnection.lastSyncAt).toLocaleString()} (
              {sourceConnection.lastSyncStatus})
            </p>
          ) : null}
          <PortfolioSyncWarning
            message={sourceConnection?.lastSyncError}
            stats={sourceConnection?.lastSyncStats}
            busy={busy}
            onConfirmClose={() => void runAction('sync-confirm')}
          />
          <PortfolioMissingPhoneAlert stats={sourceConnection?.lastSyncStats} />
          {status ? <p className='text-sm text-muted-foreground'>{status}</p> : null}
        </div>
      )}
    </div>
  );
}

function Field(props: {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <div className='space-y-1'>
      <Label htmlFor={props.id}>{props.label}</Label>
      <Input
        id={props.id}
        type={props.type}
        value={props.value}
        maxLength={128}
        onChange={event => props.onChange(event.target.value)}
      />
    </div>
  );
}
